import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTASection from '../components/CTASection';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, 
  ChevronDown, ArrowLeft, Loader2 
} from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Contact Topics - Edit to change available options
 */
const contactTopics = [
  { value: '', label: 'Select a topic...' },
  { value: 'general', label: 'General' },
  { value: 'investing', label: 'Investing & Projects' },
  { value: 'account', label: 'Account & Verification' },
  { value: 'partnerships', label: 'Partnerships' },
  { value: 'press', label: 'Press' },
  { value: 'legal', label: 'Legal/Compliance' },
];

/**
 * Contact Information - Edit to update displayed info
 */
const contactInfo = {
  email: 'support@flippex.com',
  phone: '+351 21 000 0000',
  address: 'Lisbon, Portugal'
};

const ContactPage = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    topic: '',
    message: '',
    consent: false,
    // Honeypot field
    website: ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.topic) {
      newErrors.topic = 'Please select a topic';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if filled, silently reject
    if (formData.website) {
      setFormState('success');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setFormState('submitting');
    setErrorMessage('');
    
    try {
      await axios.post(`${API_URL}/api/contact`, {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone || null,
        topic: formData.topic,
        message: formData.message,
        consent: formData.consent
      });
      
      setFormState('success');
    } catch (error) {
      console.error('Contact form error:', error);
      setFormState('error');
      setErrorMessage(
        error.response?.data?.detail || 
        'Something went wrong. Please try again or email us directly.'
      );
    }
  };

  // Success State
  if (formState === 'success') {
    return (
      <div className="min-h-screen" data-testid="contact-page">
        <Header />
        <section className="py-20">
          <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
            <div className="max-w-lg mx-auto text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Message Received!
              </h1>
              <p className="text-[rgb(var(--foreground-muted))] mb-8">
                Thanks for reaching out. We&apos;ll get back to you as soon as possible.
              </p>
              <Button
                size="lg"
                className="bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-hover))] rounded-full px-8"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Header />
      
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Contact
            </h1>
            <p className="text-lg text-[rgb(var(--foreground-muted))]">
              Send us a message and we&apos;ll get back to you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="glass p-6 md:p-8">
                <h2 className="text-xl font-heading font-semibold mb-6">
                  Send a Message
                </h2>
                
                {/* Error Banner */}
                {formState === 'error' && (
                  <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-500">Failed to send message</p>
                      <p className="text-sm text-[rgb(var(--foreground-muted))]">{errorMessage}</p>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot - hidden from real users */}
                  <div className="hidden" aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      type="text"
                      value={formData.website}
                      onChange={(e) => handleChange('website', e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Full Name */}
                  <div>
                    <Label htmlFor="fullName" className="text-sm font-medium">
                      Full Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                      placeholder="Your full name"
                      className={cn(
                        'mt-1.5',
                        errors.fullName && 'border-red-500 focus:border-red-500'
                      )}
                      data-testid="contact-name-input"
                    />
                    {errors.fullName && (
                      <p className="text-red-400 text-sm mt-1">{errors.fullName}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium">
                      Email <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="your@email.com"
                      className={cn(
                        'mt-1.5',
                        errors.email && 'border-red-500 focus:border-red-500'
                      )}
                      data-testid="contact-email-input"
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone (Optional) */}
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium">
                      Phone <span className="text-[rgb(var(--foreground-muted))]">(optional)</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="+351 XXX XXX XXX"
                      className="mt-1.5"
                    />
                  </div>

                  {/* Topic */}
                  <div>
                    <Label htmlFor="topic" className="text-sm font-medium">
                      Topic <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative mt-1.5">
                      <select
                        id="topic"
                        value={formData.topic}
                        onChange={(e) => handleChange('topic', e.target.value)}
                        className={cn(
                          'w-full h-10 px-3 py-2 rounded-md bg-white/5 border text-white appearance-none cursor-pointer',
                          'focus:outline-none focus:ring-2 focus:ring-[rgb(var(--primary))] focus:border-transparent',
                          errors.topic ? 'border-red-500' : 'border-white/10'
                        )}
                      >
                        {contactTopics.map((topic) => (
                          <option 
                            key={topic.value} 
                            value={topic.value}
                            className="bg-[rgb(var(--background))] text-white"
                          >
                            {topic.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[rgb(var(--foreground-muted))] pointer-events-none" />
                    </div>
                    {errors.topic && (
                      <p className="text-red-400 text-sm mt-1">{errors.topic}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-sm font-medium">
                      Message <span className="text-red-400">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="How can we help you?"
                      rows={5}
                      className={cn(
                        'mt-1.5 resize-none',
                        errors.message && 'border-red-500 focus:border-red-500'
                      )}
                      data-testid="contact-message-input"
                    />
                    {errors.message && (
                      <p className="text-red-400 text-sm mt-1">{errors.message}</p>
                    )}
                  </div>

                  {/* Consent Checkbox */}
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consent"
                      checked={formData.consent}
                      onCheckedChange={(checked) => handleChange('consent', checked)}
                      className="mt-0.5"
                    />
                    <Label 
                      htmlFor="consent" 
                      className="text-sm text-[rgb(var(--foreground-muted))] cursor-pointer leading-relaxed"
                    >
                      I agree to be contacted about this request and understand my data will be handled according to the Privacy Policy.
                    </Label>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-hover))] rounded-full"
                    disabled={formState === 'submitting'}
                    data-testid="contact-submit-button"
                  >
                    {formState === 'submitting' ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </div>

            {/* Contact Information Sidebar */}
            <div className="space-y-4">
              <Card className="glass p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[rgb(var(--primary))]/10 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-[rgb(var(--primary))]" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Email</h3>
                    <a 
                      href={`mailto:${contactInfo.email}`}
                      className="text-[rgb(var(--foreground-muted))] hover:text-[rgb(var(--primary))] transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="glass p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[rgb(var(--primary))]/10 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[rgb(var(--primary))]" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Location</h3>
                    <p className="text-[rgb(var(--foreground-muted))]">{contactInfo.address}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <CTASection />

      <Footer />
    </div>
  );
};

export default ContactPage;
